import React, { createContext, useState, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setIsAdmin,
  setIsUserAuthenticated,
  setLoading,
  logout as logoutAction,
} from "../features/auth/userSlice";
import { useRef } from "react";
import { getApiBaseUrl } from "../config";
import { fetchAndResolvePrivilegedAccess } from "../utils/adminCapability";
import { clearMyModulesCache } from "../utils/myModulesClient";
import { logoutZohoAsap } from "../utils/lazyZohoAsap";
import {
  buildReduxUser,
  clearAuthUser,
  fetchAuthSession,
  persistAuthUser,
} from "../utils/authSession";
import { clearSessionClock } from "../utils/sessionClock";

// Create the context
const UserContext = createContext(null);

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { currentUser, isAdmin, userCompany, loading, isUserAuthenticated } =
    useSelector((state) => state.user);
  const hasFetched = useRef(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const checkAuthStatus = async () => {
      try {
        // /auth/me and /auth/my-modules both only depend on the session cookie, not on each
        // other's response, so run them concurrently instead of waterfalling. App.js blocks
        // the entire authenticated app behind a spinner until this resolves, so the sequential
        // version was paying two full round trips back-to-back before any page could paint.
        const [user, isAdminUser] = await Promise.all([
          fetchAuthSession(),
          fetchAndResolvePrivilegedAccess(),
        ]);

        if (user) {
          persistAuthUser(user);
          const userObj = buildReduxUser(user);

          // Batch dispatch all auth updates
          dispatch(setUser(userObj));
          dispatch(setIsAdmin(isAdminUser));
          dispatch(setIsUserAuthenticated(true));

          // Initialize per-session MFA verification state
          const sessionFlag = sessionStorage.getItem("mfaVerified");
          setMfaVerified(user.mfaEnabled ? sessionFlag === "true" : true);

          // Check if user must change password
          const shouldChangePassword =
            user.mustChangePassword === true ||
            user.mustChangePassword === "true";
          setMustChangePassword(shouldChangePassword);
        } else {
          clearMyModulesCache();
          dispatch(setIsUserAuthenticated(false));
          dispatch(setIsAdmin(false));
          setMfaVerified(false);
          setMustChangePassword(false);
        }
      } catch (error) {
        console.error("Error restoring auth session:", error);
        clearMyModulesCache();
        clearAuthUser();
        dispatch(setIsUserAuthenticated(false));
        dispatch(setIsAdmin(false));
        setMfaVerified(false);
        setMustChangePassword(false);
      } finally {
        // Mark as initialized and set loading to false
        setIsInitialized(true);
        dispatch(setLoading(false));
      }
    };

    checkAuthStatus();
  }, [dispatch]);

  // Function to refresh user data from localStorage
  const refreshUser = async () => {
    try {
      const [user, isAdminUser] = await Promise.all([
        fetchAuthSession(),
        fetchAndResolvePrivilegedAccess(),
      ]);
      if (user) {
        persistAuthUser(user);
        const userObj = buildReduxUser(user);
        dispatch(setUser(userObj));
        dispatch(setIsAdmin(isAdminUser));

        const shouldChangePassword =
          user.mustChangePassword === true ||
          user.mustChangePassword === "true";
        setMustChangePassword(shouldChangePassword);
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  // Function to handle logout
  const logout = async ({ skipApi = false, delay = true } = {}) => {
    // Set loading state to show "Signing out..."
    dispatch(setLoading(true));

    if (!skipApi) {
      try {
        await fetch(`${getApiBaseUrl()}/auth/signout`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
      } catch (error) {
        console.error("Logout API error:", error);
      }
    }

    if (delay) {
      // Add a small delay to show the loading state
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    // Clear local storage and redux state
    clearAuthUser();
    clearMyModulesCache();
    clearSessionClock();
    // The Desk widget keeps its own session. Without this, the next person to sign in on
    // a shared browser inherits the previous member's ticket history.
    logoutZohoAsap();
    sessionStorage.removeItem("mfaVerified");
    setMfaVerified(false);
    setMustChangePassword(false);
    dispatch(logoutAction());
  };

  const value = {
    currentUser,
    isAdmin,
    isUserAuthenticated,
    userCompany,
    loading: loading || !isInitialized, // Keep loading true until initialized
    logout,
    refreshUser,
    mfaVerified,
    setMfaVerified,
    mustChangePassword,
    setMustChangePassword,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
